import { useState, useEffect, useMemo } from 'react';
import { Users, Search, CheckCircle, XCircle, Clock, ChevronRight, Shield, Mail, UserX, UserCheck, Trash2, Phone, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import Toast from '@/components/Toast';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import SendEmailModal from '@/components/SendEmailModal';
import { StaffAccountCreate } from '@/components/internal-staff/StaffAccountControls';
import { canManageInternal, INTERNAL_ROLE, INTERNAL_LABEL } from '@/components/internal-staff/shared';

const AdminEmployees = ({ user, activeModule = "b2b" }) => {
  const { toasts, showToast, removeToast } = useToast();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);

  useEffect(() => { fetchEmployees(); }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setEmployees(response.data.filter(u => u.role === 'employee' || (u.role === INTERNAL_ROLE && canManageInternal(user))));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine single unified status for an employee
  const getUnifiedStatus = (emp) => {
    if (emp.active === false) return 'deactivated';
    if (emp.status === 'pending') return 'pending';
    if (emp.status === 'rejected') return 'rejected';
    if (emp.status === 'approved') return 'active';
    return 'pending';
  };

  // Counts for dashboard
  const counts = useMemo(() => {
    const c = { total: 0, active: 0, deactivated: 0, pending: 0, rejected: 0 };
    employees.forEach(emp => {
      c.total++;
      const s = getUnifiedStatus(emp);
      if (c[s] !== undefined) c[s]++;
    });
    return c;
  }, [employees]);

  // Filter tabs config
  const tabs = [
    { value: 'all', label: 'Tous', count: counts.total, color: 'text-slate-600' },
    { value: 'active', label: 'Actifs', count: counts.active, color: 'text-emerald-600' },
    { value: 'pending', label: 'En attente', count: counts.pending, color: 'text-amber-600' },
    { value: 'rejected', label: 'Rejetés', count: counts.rejected, color: 'text-red-600' },
    { value: 'deactivated', label: 'Désactivés', count: counts.deactivated, color: 'text-slate-400' },
  ];

  const filteredEmployees = useMemo(() => {
    return employees
      .filter(emp => {
        if (filter === 'all') return true;
        return getUnifiedStatus(emp) === filter;
      })
      .filter(emp =>
        !search || 
        emp.name?.toLowerCase().includes(search.toLowerCase()) ||
        emp.email?.toLowerCase().includes(search.toLowerCase()) ||
        emp.last_name?.toLowerCase().includes(search.toLowerCase()) ||
        emp.first_name?.toLowerCase().includes(search.toLowerCase()) ||
        emp.phone?.includes(search)
      )
      .sort((a, b) => {
        const la = (a.last_name || a.name || '').toLowerCase();
        const lb = (b.last_name || b.name || '').toLowerCase();
        if (la !== lb) return la.localeCompare(lb);
        return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
      });
  }, [employees, filter, search]);

  const formatName = (emp) => {
    if (emp.last_name && emp.first_name) return `${emp.last_name} ${emp.first_name}`;
    return emp.name || 'Inconnu';
  };

  // Badge config by unified status
  const statusConfig = {
    active: { label: 'Actif', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    pending: { label: 'En attente', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
    rejected: { label: 'Rejeté', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
    deactivated: { label: 'Désactivé', bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', dot: 'bg-slate-400' },
  };

  // Actions
  const handleDeactivate = async (userId, name) => {
    if (!window.confirm(`Désactiver le compte de "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      await fetchEmployees();
      showToast(`${name} désactivé`, 'success');
    } catch (e) { showToast(e.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handleReactivate = async (userId, name) => {
    if (!window.confirm(`Réactiver le compte de "${name}" ?`)) return;
    try {
      await axios.put(`${API}/users/${userId}/reactivate`, {}, { withCredentials: true });
      await fetchEmployees();
      showToast(`${name} réactivé`, 'success');
    } catch (e) { showToast(e.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`SUPPRIMER DÉFINITIVEMENT "${name}" ?\n\nCette action est IRRÉVERSIBLE.`)) return;
    if (!window.confirm(`Dernière confirmation : Supprimer "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}/permanent`, { withCredentials: true });
      await fetchEmployees();
      showToast(`${name} supprimé`, 'success');
    } catch (e) { showToast(e.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handleApprove = async (userId, name) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, {}, { withCredentials: true });
      await fetchEmployees();
      showToast(`${name} approuvé`, 'success');
    } catch (e) { showToast(e.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handleReject = async (userId, name) => {
    try {
      await axios.put(`${API}/users/${userId}/reject`, {}, { withCredentials: true });
      await fetchEmployees();
      showToast(`${name} rejeté`, 'success');
    } catch (e) { showToast(e.response?.data?.detail || 'Erreur', 'error'); }
  };

  const handlePromoteAdmin = async (userId, name) => {
    if (!window.confirm(`Promouvoir "${name}" en administrateur ?`)) return;
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: 'admin', admin_level: 'full' }, { withCredentials: true });
      await fetchEmployees();
      showToast(`${name} promu administrateur`, 'success');
    } catch (e) { showToast(e.response?.data?.detail || 'Erreur', 'error'); }
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6" data-testid="admin-employees-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Gestion des Employés</h1>
            <p className="text-slate-500 text-sm mt-1">{counts.total} employé(s) enregistré(s)</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchEmployees} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
          {canManageInternal(user) && <StaffAccountCreate onCreated={fetchEmployees} />}
        </div>

        {/* KPI Dashboard */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-dashboard">
          <Card className="border-l-4 border-l-slate-800 bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{counts.total}</p>
                <p className="text-xs text-slate-500 font-medium">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500 bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{counts.active}</p>
                <p className="text-xs text-slate-500 font-medium">Actifs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500 bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{counts.pending}</p>
                <p className="text-xs text-slate-500 font-medium">En attente</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-400 bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-500">{counts.deactivated}</p>
                <p className="text-xs text-slate-500 font-medium">Désactivés</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search + Filter Tabs */}
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Rechercher par nom, email ou téléphone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="filter-tabs">
            {tabs.map(tab => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                data-testid={`tab-${tab.value}`}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-all relative ${
                  filter === tab.value
                    ? 'text-slate-900 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  filter === tab.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Employee List */}
        <div className="space-y-2" data-testid="employee-list">
          {filteredEmployees.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="w-14 h-14 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-400 font-medium">Aucun employé trouvé</p>
                <p className="text-slate-300 text-sm mt-1">Essayez de modifier vos filtres</p>
              </CardContent>
            </Card>
          ) : (
            filteredEmployees.map(emp => {
              const status = getUnifiedStatus(emp);
              const cfg = statusConfig[status];
              const name = formatName(emp);
              const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

              return (
                <Card
                  key={emp.user_id}
                  data-testid={`employee-card-${emp.user_id}`}
                  className={`transition-all hover:shadow-md ${status === 'deactivated' ? 'opacity-60 hover:opacity-100' : ''}`}
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Avatar + Info */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="relative flex-shrink-0">
                          <img
                            src={emp.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0F172A&color=fff&size=48`}
                            alt={name}
                            className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
                          />
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${cfg.dot}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900 text-base truncate">{name}</h3>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-testid={`status-badge-${emp.user_id}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                              {cfg.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 truncate mt-0.5">{emp.email}</p>
                          {emp.role === INTERNAL_ROLE && <p className="text-xs text-slate-600 mt-1" data-testid={`employee-role-${emp.user_id}`}>{INTERNAL_LABEL}</p>}
                          {emp.phone && (
                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {emp.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions - contextuelles selon le statut */}
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pl-16 sm:pl-0" data-testid={`actions-${emp.user_id}`}>
                        {/* ACTIF: Désactiver + Admin + Email + Profil */}
                        {status === 'active' && (
                          <>
                            <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200" onClick={() => handleDeactivate(emp.user_id, name)} data-testid={`deactivate-${emp.user_id}`}>
                              <UserX className="w-3.5 h-3.5 mr-1" /> Désactiver
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 text-purple-600 hover:bg-purple-50 hover:border-purple-200" onClick={() => handlePromoteAdmin(emp.user_id, name)} data-testid={`promote-${emp.user_id}`}>
                              <Shield className="w-3.5 h-3.5 mr-1" /> Admin
                            </Button>
                          </>
                        )}

                        {/* DÉSACTIVÉ: Réactiver + Supprimer */}
                        {status === 'deactivated' && (
                          <>
                            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleReactivate(emp.user_id, name)} data-testid={`reactivate-${emp.user_id}`}>
                              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Réactiver
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleDelete(emp.user_id, name)} data-testid={`delete-${emp.user_id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                            </Button>
                          </>
                        )}

                        {/* EN ATTENTE: Approuver + Rejeter */}
                        {status === 'pending' && (
                          <>
                            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApprove(emp.user_id, name)} data-testid={`approve-${emp.user_id}`}>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approuver
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleReject(emp.user_id, name)} data-testid={`reject-${emp.user_id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
                            </Button>
                          </>
                        )}

                        {/* REJETÉ: Approuver (donner une chance) + Supprimer */}
                        {status === 'rejected' && (
                          <>
                            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleApprove(emp.user_id, name)} data-testid={`approve-${emp.user_id}`}>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approuver
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleDelete(emp.user_id, name)} data-testid={`delete-${emp.user_id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                            </Button>
                          </>
                        )}

                        {/* Email - always available */}
                        {emp.email && (
                          <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 text-blue-600 hover:bg-blue-50 hover:border-blue-200" onClick={() => { setEmailTarget(emp); setShowEmailModal(true); }} data-testid={`email-${emp.user_id}`}>
                            <Mail className="w-3.5 h-3.5 mr-1" /> Email
                          </Button>
                        )}

                        {/* Profil - always available */}
                        <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => navigate(`/admin/b2b/employees/${emp.user_id}/profile`)} data-testid={`profile-${emp.user_id}`}>
                          Profil <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Results count */}
        {filteredEmployees.length > 0 && (
          <p className="text-xs text-slate-400 text-center pt-2">
            {filteredEmployees.length} résultat(s) affiché(s)
          </p>
        )}
      </div>

      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} duration={toast.duration} onClose={() => removeToast(toast.id)} />
      ))}

      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => { setShowEmailModal(false); setEmailTarget(null); }}
        employee={emailTarget}
        onEmailSent={() => showToast('Email envoyé avec succès !', 'success')}
      />
    </Layout>
  );
};

export default AdminEmployees;
